import React, {useMemo} from 'react';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {openReportExcerptInNewTab} from '../../../../../features/collection-requests/coverage/navigateToReportExcerpt';

/**
 * Renders an expert opinion's evidence text as plain prose. Persona opinions are grounded only
 * in the expert's own profile/expertise and their own prior analytical work (FINTEL) — never in
 * the OSINT/corpus evidence that backed the base forecast — so there are no numbered citation
 * badges here (contrast with the forecast body's `citedSources`-driven `[n]` markers below). Any
 * FINTEL report(s) this evidence draws on are surfaced separately, once per persona, by
 * {@link PersonaReferencesList} rather than repeated under every point.
 */
export function OpinionEvidenceText({evidence}) {
    if (!evidence) return null;

    return (
        <Typography
            variant="caption"
            color="text.secondary"
            sx={{pl: '23px', fontStyle: 'italic', display: 'block'}}
        >
            {evidence}
        </Typography>
    );
}

/**
 * A single persona opinion's referenced FINTEL reports, deduplicated across all of its
 * agreement/disagreement points and rendered once as a "References" bullet list — mirroring the
 * "Sources" list style used for the base forecast's predictions (`ForecastSourcesList`), but for
 * the expert's own prior authored reports instead of OSINT/corpus sources. Points that share the
 * same `sourceReportId` (e.g. three points all drawing on the same report) collapse into one
 * bullet rather than repeating the link under every point.
 */
export function PersonaReferencesList({points}) {
    const reports = useMemo(() => {
        const byId = new Map();
        (points || []).forEach((pt) => {
            if (pt?.sourceReportId && !byId.has(pt.sourceReportId)) {
                byId.set(pt.sourceReportId, pt.sourceReportTitle || 'Untitled report');
            }
        });
        return Array.from(byId, ([reportId, title]) => ({reportId, title}));
    }, [points]);

    if (!reports.length) return null;

    return (
        <Stack spacing={0.25} sx={{pl: '23px', pt: 0.5}}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
                References
            </Typography>
            <Stack component="ul" spacing={0.25} sx={{listStyle: 'none', m: 0, p: 0}}>
                {reports.map(({reportId, title}) => (
                    <Typography key={reportId} component="li" variant="caption" color="text.secondary">
                        <Link
                            component="button"
                            type="button"
                            variant="caption"
                            underline="hover"
                            onClick={(e) => {
                                e.stopPropagation();
                                openReportExcerptInNewTab(reportId);
                            }}
                            sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5, textAlign: 'left'}}
                        >
                            <OpenInNewRoundedIcon sx={{fontSize: 14, flexShrink: 0}}/>
                            {title}
                        </Link>
                    </Typography>
                ))}
            </Stack>
        </Stack>
    );
}

/**
 * MINSKY-16820 (ticket 02): build a stable, forecast-wide 1-based citation number for each
 * deduplicated source key, in the order the backend returned them in `forecast.sources`
 * (ticket 01's top-level dedup catalog). Numbering is derived once per forecast so every
 * prediction's markers reference the same shared numbering.
 */
export function buildForecastSourceIndex(forecast) {
    const sourcesByKey = new Map();
    const numberByKey = new Map();
    (forecast?.sources || []).forEach((source, idx) => {
        if (!source?.key) return;
        sourcesByKey.set(source.key, source);
        numberByKey.set(source.key, idx + 1);
    });
    return {sourcesByKey, numberByKey};
}

/**
 * MINSKY-16818 (ticket 02): resolve a single cited-source key into the same open-source attrs
 * shape `openCitationRefSource` (`CitationRefCard.jsx`) expects — the one place this per-key
 * resolution happens, shared by `buildCitationMarkAttrsList` (ticket 04, in-document insertion)
 * and the Forecast panel's per-item footnote list + clickable inline markers (ticket 02), so all
 * three stay in sync. Returns `null` if the key doesn't resolve to a numbered source.
 */
export function resolveForecastSourceAttrs(key, sourcesByKey, numberByKey) {
    const source = sourcesByKey?.get(key);
    const number = numberByKey?.get(key);
    if (!source || !number) return null;
    return {
        key,
        number,
        kind: source.kind || null,
        title: source.title || null,
        snippet: source.snippet || null,
        documentType: source.documentType || null,
        referenceId: source.referenceId ?? null,
        canonicalFileId: source.canonicalFileId ?? null,
        docId: source.docId ?? null,
        segmentIndex: source.segmentIndex ?? null,
        sentenceHit: source.sentenceHit || null,
    };
}

/**
 * MINSKY-16823 (ticket 04): resolve a prediction's `citedSources` keys into the ordered list of
 * `citationRef` mark attrs to insert into the report body — pure/PM-schema-agnostic so it's unit
 * testable without a full ProseMirror EditorState. Reuses the SAME `sourcesByKey`/`numberByKey`
 * numbering as the panel preview (ticket 02) so numbers are consistent everywhere for this
 * forecast, while still starting fresh at 1 relative to any other citations already in the
 * document (this function never looks at document content).
 */
export function buildCitationMarkAttrsList(citedSources, sourcesByKey, numberByKey) {
    if (!citedSources?.length) return [];
    const attrsList = [];
    citedSources.forEach((key) => {
        const attrs = resolveForecastSourceAttrs(key, sourcesByKey, numberByKey);
        if (attrs) attrsList.push(attrs);
    });
    return attrsList;
}
